import {
  faListCheck,
  faMicrophone,
  faStar,
  faFlag,
  faBullseye,
  faUserCheck,
  faUserClock,
  faCrown,
  faMedal,
  faChartLine,
  faCheckCircle,
  faUsers
} from "@fortawesome/free-solid-svg-icons";

export const founding50RoleDescription = {
  roleId: 'founding50',
  title: 'Founding 50',
  sections: [
    // Overview.
    {
      id: 'overview',
      title: 'Overview',
      displayTitle: 'Founding 50',
      subtitle: 'Enter early. Compete fully. Get ready.',
      description: 'The most exclusive and competitive role in Spitface Battles beta. Built for battlers who want full participation across battles, judging, audience scoring, and community activity. Every part of the system is active here, and your involvement spans all of it. If you\'re joining the Founding 50, be ready to engage on every front.',
      contentItems: [
        '**Beta Window:** 12 months or until V1 launch',
        '**Signup Capacity:** 150'
      ],
      highlights: [
        "50 Official Spots",
        "High Commitment",
        "Performance-Based"
      ],
      moreItems: [{
        key: "battling",
        label: "Battling",
        icon: faMicrophone
      },
      {
        key: "judging",
        label: "Judging",
        icon: faListCheck
      },
      {
        key: "community",
        label: "Engagement",
        icon: faUsers
      },
      {
        key: "feedback",
        label: "Feedback",
        icon: faStar
      }]
    },
    // Why This Role Matters.
    {
      id: 'whyThisRoleMatters',
      title: 'Why This Role Matters',
      displayTitle: '',
      subtitle: 'Impact. Direction. Legacy.',
      description: 'The Founding 50 define competitive standards and set the tone for Spitface Battles. Your presence drives how the community develops and how the platform evolves. What happens here carries forward beyond beta and becomes part of the foundation others build on. This is where your impact lasts. Make it legendary.',
      cards: [
        {
          id: 'influenceImpact',
          title: 'Influence & Impact',
          icon: faFlag,
          items: [
            'Influence platform direction.',
            'Drive community development.'
          ]
        },
        {
          id: 'highLevelCompetition',
          title: 'High-Level Competition',
          icon: faBullseye,
          items: [
            'The most competitive beta role.',
            'Strong performance sets the tone.'
          ]
        },
        {
          id: 'prestigeLegacy',
          title: 'Prestige & Legacy',
          icon: faStar,
          items: [
            'Earn permanent recognition.',
            'Your presence becomes SFB history.'
          ]
        }
      ]
    },
    // Who Should Apply.
    { 
      "id": "whoShouldApply",
      "title": "Who Should Apply",
      "displayTitle": "",
      subtitle: 'Ready. Reliable. Competitive.',
      description: 'This role is built for battlers who are ready to participate across multiple areas. It requires reliability, commitment, and the ability to follow through over time. Make sure you\'re prepared before stepping in.',
      "lists": [{
        "title": "Ideal For:",
        "items": [
          'Already active in battle rap',
          'Able to commit to ongoing participation',
          'Seeking a structured, high-impact role'
        ]
      }, {
        "title": "Not Ideal For:",
        "items": [
          'Low-commitment or occasional participation',
          'Inconsistent availability or difficulty meeting recurring expectations'
        ]
      }]
    },
    // What You'll Do.
    {
      id: 'whatYoullDo',
      title: 'What You\'ll Do',
      displayTitle: '',
      subtitle: 'Battle. Judge. Stay Active.',
      description: 'This role requires active participation across all core areas of the platform. You will enter battles, participate in judging, contribute audience scorecards, and stay engaged in ongoing discussion. Each area requires follow-through and consistent involvement over time. Show up and deliver.',
      tiles: [{
          id: 'battling',
          icon: faMicrophone,
          title: 'Battling',
          label: 'Enter battles and submit verses on time'
        }, {
          id: 'judging',
          icon: faListCheck,
          title: 'Judging',
          label: 'Judge battles and submit scorecards on time'

        }, {
          id: 'audience',
          icon: faUserCheck,
          title: 'Audience Scoring',
          label: 'Add scorecards outside of official judging'
        },  {
          id: 'engagement',
          icon: faUsers,
          title: 'Engagement',
          label: 'Participate in discussions and provide feedback'
        }]
    },
    // What You'll Get.
    {
      id: 'whatYoullGet',
      title: 'What You\'ll Get',
      displayTitle: '',
      subtitle: 'Status. Recognition. Influence.',
      description: 'Founding 50 members receive exclusive status, permanent recognition, and early access to platform features. Each tier provides defined benefits, from premium features to Hall of Fame placement and special badges. These rewards highlight your position as one of the first to shape the Spitface Battles experience.',
      contentItems: ['**Important:** Full beta participation earns top consideration for official status, subject to role limits.'],
      tiles: [
        {
          id: 'official',
          icon: faCrown,
          title: 'Founding 50',
          label: 'Top 50 provisional members unlock lifetime premium access, Hall of Fame placement, and a permanent Founding 50 badge and battle marker.'
        },
        {
          id: 'runnerUp',
          icon: faMedal,
          title: 'Runner-Ups',
          label: 'Up to 4 years of premium access, Hall of Fame placement, and priority consideration for community leadership.'
        },
        {
          id: 'provisional',
          icon: faUserClock,
          title: 'Provisional Members',
          label: 'Gain gradual premium access, a temporary Founding 50 battle marker, and influence on the platform during beta.'
        }
      ]
    },
    // Requirements.
    {
      id: 'requirements',
      title: 'Requirements',
      displayTitle: '',
      subtitle: 'Compete. Contribute. Stay consistent.',
      description: 'Founding 50 status is earned through sustained performance, engagement, and contribution. Every metric — battling, judging, audience scoring, and community engagement — counts toward your status. Show up, deliver, and maintain the standards to keep your spot.',
      tiles: [{
        id: 'standards',
        icon: faChartLine,
        title: 'Performance Standards',
        label: 'Maintain 90% consistency across all metrics. Status is earned through sustained performance.'
      }, 
      {
        id: 'markers',
        icon: faCheckCircle,
        title: 'Consistency Markers',
        label: 'Complete your battles, judging, and scoring targets. Stay active in discussions and contribute meaningfully to the community.'
      }],
      contentItems: [
        '**Important:** Metrics and thresholds may be adjusted before closed beta launch.'
      ],
      tables: [{
        title: 'Requirements by Phase',
        headers: [
          '',
          'phase 1\nclosed beta',
          'phase 2\nopen beta',
          'phase 3\nopen beta',
        ],
        rows: [{
          requirement: 'battling',
          metric: 'verses',
          timeframe: 'monthly',
          phase1: '5',
          phase2: '3',
          phase3: '5',
          execution: 'Enter and complete battles consistently each month'

        }, {
          requirement: 'judging',
          metric: 'scorecards',
          timeframe: 'monthly',
          phase1: '2',
          phase2: '1',
          phase3: '2',
          execution: 'Submit complete, fair judging scorecards as a judge'

        }, {
          requirement: 'audience',
          metric: 'scorecards',
          timeframe: 'total',
          phase1: '24',
          phase2: '60',
          phase3: '24',
          execution: 'Submit audience scorecards outside official judging targets'

        }, {
          requirement: 'engagement',
          metric: 'comments',
          timeframe: 'weekly',
          phase1: '10',
          phase2: '5',
          phase3: '10',
          execution: 'Leave constructive comments on battles, discussions, and feedback threads to support community and competitive analysis.'

        }]
      }]
    },
    // Join Now.
    {
      id: 'joinNow',
      title: 'Join Now',
      displayTitle: '',
      subtitle: 'Confirm. Commit. Start Now.',
      description: 'Confirm you understand the Founding 50 role requirements, eligibility, rewards, and community standards before registering.',
      checkboxes: [
        {
          id: 'commitmentPerformanceV1',
          label: 'I understand rewards start at 3 months and status is earned through consistent performance.'
        },
        {
          id: 'trackingAndEligibilityV1',
          label: 'I understand leaderboards track eligibility, contribution, and progression.'
        },
        {
          id: 'communityEngagementV1',
          label: 'I will stay active, contribute to discussions, and participate in Discord during beta.'
        },
        {
          id: 'requirementsAndStatusV1',
          label: 'I understand requirements may evolve and final status depends on performance across all metrics.'
        }
      ]
    }
  ]
};
// 